import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Grid,
  Divider,
  ListSubheader,
  Tabs,
  Tab,
  Stack,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper
} from '@mui/material';
import { addNode, createNodeRequest, NodeType } from '../api/services/computeService/NodeService';
import { getAvailableRegions } from '../api/services/computeService/LocationService';
import { useKeycloak } from '@react-keycloak/web';
import { UploadFile as UploadFileIcon } from '@mui/icons-material';

const AddNodeForm = ({ open, onClose, onSuccess }) => {
  const { keycloak } = useKeycloak();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const availableRegions = getAvailableRegions();
  const [mode, setMode] = useState('single');
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploadError, setBulkUploadError] = useState('');
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResults, setBulkResults] = useState([]);

  const [formData, setFormData] = useState({
    ipAddress: '',
    port: '22',
    region: 'USA',
    nodeType: NodeType.NODE_TYPE_COMPUTE,
    username: 'username',
    password: 'password',
    privateKey: ''
  });

  const handleChange = (field) => (event) => {
    let value = event.target.value;

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (mode !== 'single') return;

    if (!formData.ipAddress || !formData.username) {
      setError('IP Address and Username are required');
      return;
    }

    if (!formData.password && !formData.privateKey) {
      setError('Please provide either a password or an SSH private key for authentication');
      return;
    }

    if (!keycloak.token) {
      setError('Authentication required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await keycloak.updateToken(30);
      
      const nodeRequest = createNodeRequest(formData);
      const response = await addNode({
        ...nodeRequest,
        token: keycloak.token
      });

      if (response.success) {
        onSuccess && onSuccess(response.data);
        handleClose();
      } else {
        setError(response.error || 'Failed to add node');
      }
    } catch (error) {
      console.error('Add node error:', error);
      setError('Failed to add node: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      ipAddress: '',
      port: '22',
      region: 'USA',
      nodeType: NodeType.NODE_TYPE_COMPUTE,
      username: 'username',
      password: 'password',
      privateKey: ''
    });
    setError('');
    setMode('single');
    setBulkFile(null);
    setBulkUploadError('');
    setBulkResults([]);
    setBulkUploading(false);
    onClose();
  };

  const parseNodeTypeValue = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const normalized = value.toString().toLowerCase();
    if (normalized.includes('storage') || normalized === '1' || normalized === NodeType.NODE_TYPE_STORAGE.toString()) return NodeType.NODE_TYPE_STORAGE;
    if (normalized.includes('compute') || normalized === '2' || normalized === NodeType.NODE_TYPE_COMPUTE.toString()) return NodeType.NODE_TYPE_COMPUTE;
    return null;
  };

  const normalizeServerRow = (row) => {
    const ipAddress = row.ipAddress || row.ip || row.hostname || row.address || row.server;
    const username = row.username || row.user;
    const password = row.password || row.pass;
    const privateKey = row.privateKey || row.key;
    const region = row.region || row.Region || row.country;
    const nodeType = parseNodeTypeValue(row.nodeType || row.type);
    if (!ipAddress || !username || (!password && !privateKey)) return null;
    if (!region || !nodeType) return null;
    return {
      ipAddress: ipAddress.toString().trim(),
      port: '22',
      region: region.toString().trim(),
      nodeType,
      username: username.toString().trim(),
      password: (password || '').toString(),
      privateKey: (privateKey || '').toString()
    };
  };

  const readFileAsText = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Unable to read file'));
    reader.readAsText(file);
  });

  const readFileAsArrayBuffer = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Unable to read file'));
    reader.readAsArrayBuffer(file);
  });

  const parseCsvText = (text) => {
    const rows = text.split(/\r?\n/).map(r => r.trim()).filter(Boolean);
    if (!rows.length) return [];
    const delimiter = rows.some(r => r.includes(';')) ? ';' : ',';
    const headerParts = rows[0].split(delimiter).map(v => v.trim().toLowerCase());
    const hasHeader = headerParts.some(h => ['ip', 'ipaddress', 'hostname', 'address', 'server'].includes(h));
    if (hasHeader) {
      return rows.slice(1).map(line => {
        const cells = line.split(delimiter).map(c => c.trim());
        const data = {};
        headerParts.forEach((h, idx) => {
          data[h] = cells[idx] || '';
        });
        return normalizeServerRow({
          ipAddress: data.ipaddress || data.ip || data.hostname || data.address || data.server,
          username: data.username || data.user,
          password: data.password || data.pass,
          privateKey: data.privatekey || data.key,
          region: data.region,
          nodeType: data.nodetype || data.type
        });
      }).filter(Boolean);
    }
    return rows.map(line => {
      const cells = line.split(delimiter).map(c => c.trim());
      return normalizeServerRow({
        ipAddress: cells[0],
        username: cells[1],
        password: cells[2],
        privateKey: cells[6],
        port: '22',
        region: cells[4],
        nodeType: cells[5]
      });
    }).filter(Boolean);
  };

  const parseExcelFile = async (file) => {
    const xlsxModule = await import('xlsx');
    const xlsx = xlsxModule.default || xlsxModule;
    const data = await readFileAsArrayBuffer(file);
    const workbook = xlsx.read(data, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = xlsx.utils.sheet_to_json(sheet, { defval: '' });
    return rows.map(row => normalizeServerRow({
      ipAddress: row.ipAddress || row.IP || row.ip || row.hostname || row.address || row.server,
      username: row.username || row.user,
      password: row.password || row.pass,
      privateKey: row.privateKey || row.key,
      region: row.region,
      nodeType: row.nodeType || row.type
    })).filter(Boolean);
  };

  const parseServersFromFile = async (file) => {
    const extension = file.name.toLowerCase().split('.').pop();
    if (['xlsx', 'xls'].includes(extension)) {
      return parseExcelFile(file);
    }
    const text = await readFileAsText(file);
    return parseCsvText(text || '');
  };

  const handleBulkFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    setBulkFile(file || null);
    setBulkUploadError('');
    setBulkResults([]);
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      setBulkUploadError('Select a CSV or Excel file first');
      return;
    }
    if (!keycloak.token) {
      setBulkUploadError('Authentication required');
      return;
    }
    setBulkUploading(true);
    setBulkUploadError('');
    setBulkResults([]);
    try {
      await keycloak.updateToken(30);
      const parsedServers = await parseServersFromFile(bulkFile);
      if (!parsedServers.length) {
        setBulkUploadError('No valid servers found in the file');
        return;
      }
      const results = [];
      for (const server of parsedServers) {
        try {
          const nodeRequest = createNodeRequest(server);
          const response = await addNode({ ...nodeRequest, token: keycloak.token });
          results.push({
            ipAddress: server.ipAddress,
            success: response.success,
            error: response.success ? '' : response.error || 'Failed to add'
          });
        } catch (error) {
          results.push({
            ipAddress: server.ipAddress,
            success: false,
            error: error.message || 'Failed to add'
          });
        }
      }
      setBulkResults(results);
      const successCount = results.filter(r => r.success).length;
      if (successCount > 0) {
        onSuccess && onSuccess();
      }
    } catch (error) {
      setBulkUploadError(error.message || 'Failed to process the file');
    } finally {
      setBulkUploading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Add Nodes</DialogTitle>
      <Tabs value={mode} onChange={(e, val) => setMode(val)} variant="fullWidth">
        <Tab value="single" label="Single Node" />
        <Tab value="bulk" label="Bulk Upload" />
      </Tabs>
      <form onSubmit={mode === 'single' ? handleSubmit : (e) => e.preventDefault()}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {bulkUploadError && mode === 'bulk' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bulkUploadError}
            </Alert>
          )}
          
          {mode === 'single' && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Address Details
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="IP Address"
                  value={formData.ipAddress}
                  onChange={handleChange('ipAddress')}
                  required
                  placeholder="80.239.149.57"
                  helperText="The IP address of the node"
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Port"
                  type="number"
                  value={formData.port}
                  onChange={handleChange('port')}
                  required
                  helperText="SSH port (default: 22)"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={formData.region}
                    onChange={handleChange('region')}
                    label="Country"
                  >
                    <ListSubheader sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      🌎 Americas
                    </ListSubheader>
                    {availableRegions
                      .filter(country => ['USA', 'CAN', 'MEX', 'BRA', 'ARG', 'CHL', 'COL', 'PER', 'URY', 'PRY', 'ECU', 'BOL'].includes(country.value))
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .map((country) => (
                        <MenuItem key={country.value} value={country.value}>
                          <Typography variant="body2">
                            {country.label}
                          </Typography>
                        </MenuItem>
                      ))}

                    <ListSubheader sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      🌍 Europe
                    </ListSubheader>
                    {availableRegions
                      .filter(country => ['GBR', 'FRA', 'DEU', 'ITA', 'ESP', 'NLD', 'BEL', 'CHE', 'AUT', 'SWE', 'NOR', 'DNK', 'FIN', 'POL', 'CZE', 'HUN', 'PRT', 'GRC', 'IRL'].includes(country.value))
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .map((country) => (
                        <MenuItem key={country.value} value={country.value}>
                          <Typography variant="body2">
                            {country.label}
                          </Typography>
                        </MenuItem>
                      ))}

                    <ListSubheader sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      🌏 Asia Pacific
                    </ListSubheader>
                    {availableRegions
                      .filter(country => ['JPN', 'CHN', 'KOR', 'IND', 'SGP', 'THA', 'MYS', 'IDN', 'PHL', 'VNM', 'AUS', 'NZL', 'PAK', 'BGD'].includes(country.value))
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .map((country) => (
                        <MenuItem key={country.value} value={country.value}>
                          <Typography variant="body2">
                            {country.label}
                          </Typography>
                        </MenuItem>
                      ))}

                    <ListSubheader sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      🌍 Middle East & Africa
                    </ListSubheader>
                    {availableRegions
                      .filter(country => ['SAU', 'ARE', 'ISR', 'TUR', 'EGY', 'ZAF', 'NGA', 'KEN', 'MAR', 'TUN', 'JOR', 'LBN', 'IRN', 'IRQ', 'QAT', 'KWT'].includes(country.value))
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .map((country) => (
                        <MenuItem key={country.value} value={country.value}>
                          <Typography variant="body2">
                            {country.label}
                          </Typography>
                        </MenuItem>
                      ))}

                    <ListSubheader sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      🌍 Other Countries
                    </ListSubheader>
                    {availableRegions
                      .filter(country => ![
                        'USA', 'CAN', 'MEX', 'BRA', 'ARG', 'CHL', 'COL', 'PER', 'URY', 'PRY', 'ECU', 'BOL',
                        'GBR', 'FRA', 'DEU', 'ITA', 'ESP', 'NLD', 'BEL', 'CHE', 'AUT', 'SWE', 'NOR', 'DNK', 'FIN', 'POL', 'CZE', 'HUN', 'PRT', 'GRC', 'IRL',
                        'JPN', 'CHN', 'KOR', 'IND', 'SGP', 'THA', 'MYS', 'IDN', 'PHL', 'VNM', 'AUS', 'NZL', 'PAK', 'BGD',
                        'SAU', 'ARE', 'ISR', 'TUR', 'EGY', 'ZAF', 'NGA', 'KEN', 'MAR', 'TUN', 'JOR', 'LBN', 'IRN', 'IRQ', 'QAT', 'KWT'
                      ].includes(country.value))
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .map((country) => (
                        <MenuItem key={country.value} value={country.value}>
                          <Typography variant="body2">
                            {country.label}
                          </Typography>
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Node Type</InputLabel>
                  <Select
                    value={formData.nodeType}
                    onChange={handleChange('nodeType')}
                    label="Node Type"
                  >
                    <MenuItem value={NodeType.NODE_TYPE_COMPUTE}>Compute</MenuItem>
                    <MenuItem value={NodeType.NODE_TYPE_STORAGE}>Storage</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  SSH Credentials
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Provide at least one authentication method (password or SSH key). You can provide both for flexibility.
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Username"
                  value={formData.username}
                  onChange={handleChange('username')}
                  required
                  helperText="SSH username for the node"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange('password')}
                  helperText="SSH password (optional if SSH key is provided)"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="SSH Private Key (Optional)"
                  multiline
                  rows={6}
                  value={formData.privateKey}
                  onChange={handleChange('privateKey')}
                  placeholder="-----BEGIN OPENSSH PRIVATE KEY-----
MIIEowIBAAKCAQEA...
-----END OPENSSH PRIVATE KEY-----"
                  helperText="SSH private key (optional if password is provided) - supports RSA, ECDSA, and Ed25519 keys"
                  sx={{
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem'
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Authentication Options:</strong>
                    <br />
                    • You can provide either a password or SSH key (or both)
                    <br />
                    • If using SSH key, ensure the corresponding public key is in the server's authorized_keys
                    <br />
                    • SSH key authentication is generally more secure than password authentication
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          )}

          {mode === 'bulk' && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2">
                Upload CSV, TXT, or Excel with required columns: ipAddress, username, region, nodeType, and either password or privateKey. Port is fixed to 22. nodeType accepts compute or storage (or 2/1). Region expects ISO country codes like USA, DEU, SGP.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip label="nodeType: compute / 2" color="primary" size="small" />
                <Chip label="nodeType: storage / 1" color="secondary" size="small" />
                <Chip label="region: ISO3 (e.g., USA, DEU, SGP)" color="default" size="small" />
              </Stack>
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ipAddress</TableCell>
                      <TableCell>username</TableCell>
                      <TableCell>region</TableCell>
                      <TableCell>nodeType</TableCell>
                      <TableCell>password</TableCell>
                      <TableCell>privateKey</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>203.0.113.5</TableCell>
                      <TableCell>admin</TableCell>
                      <TableCell>USA</TableCell>
                      <TableCell>compute</TableCell>
                      <TableCell>secret</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>198.51.100.17</TableCell>
                      <TableCell>root</TableCell>
                      <TableCell>DEU</TableCell>
                      <TableCell>storage</TableCell>
                      <TableCell></TableCell>
                      <TableCell>-----BEGIN OPENSSH PRIVATE KEY-----...</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>192.0.2.44</TableCell>
                      <TableCell>gpuuser</TableCell>
                      <TableCell>SGP</TableCell>
                      <TableCell>2</TableCell>
                      <TableCell>p@55w0rd</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
              <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                Choose File
                <input type="file" hidden accept=".csv,.xlsx,.xls,.txt" onChange={handleBulkFileChange} />
              </Button>
              {bulkFile && (
                <Typography variant="body2" color="text.secondary">
                  Selected file: {bulkFile.name}
                </Typography>
              )}
              {bulkResults.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    {bulkResults.filter(r => r.success).length} of {bulkResults.length} server(s) accepted
                  </Alert>
                  <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
                    {bulkResults.map((result, index) => (
                      <Box key={`${result.ipAddress}-${index}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                        <Typography variant="body2">{result.ipAddress}</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip label={result.success ? 'Added' : 'Failed'} color={result.success ? 'success' : 'error'} size="small" />
                          {!result.success && (
                            <Typography variant="caption" color="error.main">
                              {result.error}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          {mode === 'single' && (
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading}
            >
              {loading ? 'Adding Node...' : 'Add Node'}
            </Button>
          )}
          {mode === 'bulk' && (
            <Button 
              type="button" 
              variant="contained" 
              onClick={handleBulkUpload}
              disabled={bulkUploading || !bulkFile}
            >
              {bulkUploading ? 'Uploading...' : 'Upload'}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddNodeForm; 